import React, { useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import { makeDebounce } from '../FrictionlessLogin/debounce';
import { useReportFrictionlessEvent } from '../FrictionlessLogin/useFrictionlessLoginLogs';

import LabeledInputWithErrors from './Form/LabeledInputWithErrors';
import FrictionlessSignInPhase3CTA from './FrictionlessSignInPhase3CTA';
import { IntuitAccountBanner } from './IntuitAccountBanner';
import { showPhoneWithoutCountryCode } from './helpers';
import { useDebounceSelector } from './hooks/useDebounceSelector';
import { useFormHandlers } from './hooks/useFormHandlers';

import { InfoCircle } from 'components/Shared/Icons/Icons';
import Tooltip from 'components/Shared/Tooltip/Tooltip';
import { useUsernameAvailability } from 'hooks/useUsernameAvailability';
import SegmentIO from 'reporting/SegmentIO';
import {
  frictionlessCheckoutContrySelector,
  frictionlessCheckoutEmailSelector,
  frictionlessCheckoutPhoneSelector,
  frictionlessLoginPreviouslySavedEmailSelector,
  isFrictionlessCheckoutDetailsModalClosedSelector,
  isFrictionlessCheckoutDetailsRequiredSelector,
  isFrictionlessCheckoutEmailValidSelector,
  isFrictionlessCheckoutPhoneValidSelector,
  isIdentitySignInWidgetErrorSelector,
  isSignInCTADecisionPendingSelector,
  shouldShowSignInCTASelector,
  isGenerateFactorLoadingSelector,
} from 'store/auth/selectors';
import { authActions, handleEmailChangeForFrictionlessLogin } from 'store/auth/slice';
import { isFrictionlessPhase2SubscriptionsEnabled } from 'store/ixp/selector';
import {
  countrySelector,
  isContactDetailsEmailValidSelector,
  isContactDetailsNameValidSelector,
} from 'store/payment/selectors';
import {
  setContactDetailsEmail,
  setContactDetailsName,
  validateContactDetailsEmail,
  validateContactDetailsName,
} from 'store/payment/slice';
import { saleSelectors } from 'store/sale/selectors';
import { useSelector } from 'store/utils';
import { colors, fontSize, breakpoints } from 'styles/cp';

interface FrictionlessDetailsProps {
  isEmailUpdateRequired?: boolean;
  showPaymentMethodsHeader?: boolean;
  shouldShowContactName?: boolean;
  isSubscription?: boolean;
  isForcedAutopay?: boolean;
  isFrictionlessPhase3Enabled?: boolean;
  cdn?: string;
  updateFrictionlessLoginCountry?: Function;
}

const FrictionlessDetails: React.FC<FrictionlessDetailsProps> = ({
  isEmailUpdateRequired,
  showPaymentMethodsHeader,
  shouldShowContactName,
  isSubscription = false,
  isForcedAutopay = false,
  isFrictionlessPhase3Enabled,
  cdn,
  updateFrictionlessLoginCountry,
}) => {
  const dispatch = useDispatch();
  const emailsFromSale = useSelector((state) => saleSelectors.toEmailsSelector(state.sale));
  const frictionlessCheckoutEmail = useSelector(frictionlessCheckoutEmailSelector);
  const frictionlessCheckoutPhone = useSelector(frictionlessCheckoutPhoneSelector);
  const isContactDetailsEmailValid = useSelector(isContactDetailsEmailValidSelector);
  const isContactDetailsNameValid = useSelector(isContactDetailsNameValidSelector);
  const email = frictionlessCheckoutEmail;
  const savedEmail = useSelector(frictionlessLoginPreviouslySavedEmailSelector);

  const isEmailValid = useDebounceSelector(isFrictionlessCheckoutEmailValidSelector);
  const isPhoneValid = useDebounceSelector(isFrictionlessCheckoutPhoneValidSelector);
  const { blurredFields, handleBlur, handleFocus, handleChange } = useFormHandlers();

  const isCheckoutDetailsModalClosed = useSelector(
    isFrictionlessCheckoutDetailsModalClosedSelector
  );
  const isCheckoutDetailsRequired = useSelector(isFrictionlessCheckoutDetailsRequiredSelector);
  const country = useSelector(countrySelector);
  const frictionlessCheckoutCountry = useSelector(frictionlessCheckoutContrySelector);
  const countryField = frictionlessCheckoutCountry.a3 ? frictionlessCheckoutCountry : country;
  const phone = showPhoneWithoutCountryCode({
    phone: frictionlessCheckoutPhone,
    frictionlessCheckoutCountry,
  });

  const frictionlessLogger = useReportFrictionlessEvent();
  const intl = useIntl();
  const debounce = makeDebounce();

  // Check if phase 2 is enabled for subscription frictionless login
  const isSubscriptionFLPhase2 = useSelector(isFrictionlessPhase2SubscriptionsEnabled);

  // Determine if username availability check should be performed
  // Check for: ForcedAutopay, FL2 subscriptions, FL3 invoices
  // Don't check for: FL2 invoices (when none of the above conditions are true)
  const shouldCheckUsernameAvailability =
    isSubscriptionFLPhase2 || isForcedAutopay || !!isFrictionlessPhase3Enabled;

  // Check if email is available (true = available/free, false = taken/exists)
  const { isAvailable: isEmailAvailable, isLoading: isEmailAvailabilityLoading } =
    useUsernameAvailability({
      email,
      shouldCheckAvailability: shouldCheckUsernameAvailability,
    });

  // Get generateFactor loading state for subscription phase 2
  const isGenerateFactorLoading = useSelector(isGenerateFactorLoadingSelector);

  // Clear frictionless login state on component mount to prevent persistence after refresh
  useEffect(() => {
    if (isSubscriptionFLPhase2 || isForcedAutopay) {
      dispatch(authActions.clearFrictionlessLoginState());
    }
  }, [isSubscriptionFLPhase2, isForcedAutopay, dispatch]);

  // Handle registration requirement when email availability changes for both subscription and forced autopay
  useEffect(() => {
    if (
      (isSubscriptionFLPhase2 || isForcedAutopay) &&
      !isEmailAvailabilityLoading &&
      isEmailAvailable !== null
    ) {
      dispatch(
        authActions.setFrictionlessUserNameAvailable({
          isUserNameAvailable: isEmailAvailable,
        })
      );

      // If email exists (isEmailAvailable is false) and frictionless flow is enabled, trigger frictionless login flow
      if (!isEmailAvailable && email && email.trim() !== '') {
        // Use the new action to handle frictionless login for existing users
        // @ts-ignore
        dispatch(handleEmailChangeForFrictionlessLogin());
      }
    }
  }, [
    isSubscriptionFLPhase2,
    isForcedAutopay,
    isEmailAvailabilityLoading,
    isEmailAvailable,
    email,
    dispatch,
  ]);

  // Show banner when it's a subscription or forced autopay and email is available (user doesn't exist/email is free)
  const shouldShowBanner = (isSubscription || isForcedAutopay) && isEmailAvailable;

  // Track banner shown event for new emails
  useEffect(() => {
    if (shouldShowBanner && email && email.trim() !== '') {
      SegmentIO.frictionlessSubscriptionPhoneFieldGuidanceShown();
    }
  }, [shouldShowBanner, email]);

  const shouldShowSignInCTAState = useSelector(shouldShowSignInCTASelector);
  const isSignInCTADecisionPending = useSelector(isSignInCTADecisionPendingSelector);
  const isIdentitySignInWidgetError = useSelector(isIdentitySignInWidgetErrorSelector);
  const isFrictionlessPhase3Eligible =
    isFrictionlessPhase3Enabled && !isSubscription && !isIdentitySignInWidgetError;
  const shouldShowSignInCTA =
    isFrictionlessPhase3Eligible && shouldShowSignInCTAState && !isSignInCTADecisionPending;

  const emailError =
    (blurredFields.email && !isEmailValid && email.trimEnd() !== '') ||
    (isCheckoutDetailsModalClosed && !isEmailValid && email.trimEnd() !== '') ||
    (isEmailUpdateRequired && !isContactDetailsEmailValid && email.trimEnd() !== '') ||
    ((isSubscription || isForcedAutopay) &&
      isCheckoutDetailsRequired &&
      (!isEmailValid || email.trimEnd() === ''))
      ? intl.formatMessage({ id: 'FRICTIONLESS_INVALID_EMAIL', defaultMessage: 'Invalid email' })
      : '';
  const phoneError =
    (blurredFields.phone && !isPhoneValid && phone.trimEnd() !== '') ||
    (isCheckoutDetailsModalClosed && !isPhoneValid && phone.trimEnd() !== '') ||
    ((isSubscription || isForcedAutopay) &&
      isCheckoutDetailsRequired &&
      (!isPhoneValid || phone.trimEnd() === ''))
      ? intl.formatMessage({
          id: 'FRICTIONLESS_INVALID_PHONE_NUMBER',
          defaultMessage: 'Invalid phone number',
        })
      : '';

  const nameError =
    blurredFields.name && !isContactDetailsNameValid
      ? intl.formatMessage({
          id: 'CONTACT_DETAILS_NAME_ERROR',
          defaultMessage: 'Name must consist of at least 2 words.',
        })
      : '';

  const shouldShowLoadingSpinner =
    (isSubscription || isForcedAutopay || isFrictionlessPhase3Eligible) &&
    (isEmailAvailabilityLoading || (isSubscriptionFLPhase2 && isGenerateFactorLoading));

  useEffect(() => {
    // since this component flashes until the state is resolved, we need to only fire if the component has not closed
    let shouldFire = true;
    setTimeout(() => {
      if (shouldFire) {
        frictionlessLogger.trackSignupViewed();
      }
    }, 100);
    return () => {
      shouldFire = false;
    };
  }, []);

  useEffect(() => {
    // Rules to set email field:
    // 1. If savedEmail exists, set it
    // 2. If there is only 1 recipient email for the sale, set it
    // 3. If there are multiple recipient emails / no emails at all, set it to an empty string
    if (savedEmail) {
      dispatch(authActions.setFrictionlessCheckoutEmail({ email: savedEmail }));
    } else if (emailsFromSale.length === 1) {
      dispatch(authActions.setFrictionlessCheckoutEmail({ email: emailsFromSale[0] }));
    } else if (emailsFromSale.length > 1) {
      dispatch(authActions.setFrictionlessCheckoutEmail({ email: '' }));
    }
  }, [savedEmail, emailsFromSale]);

  // Handle sign-in CTA decision when email check completes
  useEffect(() => {
    if (isEmailAvailabilityLoading) {
      return;
    }
    debounce(() => {
      if (!isFrictionlessPhase3Eligible) {
        dispatch(authActions.setIsSignInCTADecisionPending({ isSignInCTADecisionPending: false }));
        SegmentIO.frictionlessLoginPhase3EligibilityCheck('failed');
        return;
      }
      if (isEmailAvailabilityLoading === false) {
        dispatch(authActions.setIsSignInCTADecisionPending({ isSignInCTADecisionPending: false }));
        if (isEmailAvailable === true) {
          SegmentIO.frictionlessLoginPhase3EligibilityCheck('failed');
          return;
        }
        if (isEmailAvailable === false) {
          SegmentIO.frictionlessLoginPhase3EligibilityCheck('success');
          SegmentIO.frictionlessLoginPhase3SignInCTAViewed();
          dispatch(
            authActions.setFrictionlessCheckoutSelected({ frictionlessCheckoutSelected: false })
          );
          dispatch(authActions.setShouldShowSignInCTA({ shouldShowSignInCTA: true }));
          frictionlessLogger.trackSignInCTAViewed();
          return;
        }
      }
    }, 350);
  }, [isFrictionlessPhase3Eligible, isEmailAvailabilityLoading, isEmailAvailable, dispatch]);

  useEffect(() => {
    const shouldBlockCheckoutSelection = isFrictionlessPhase3Eligible && isEmailAvailable === false;
    dispatch(
      authActions.setFrictionlessCheckoutSelected({
        frictionlessCheckoutSelected: isEmailValid && isPhoneValid && !shouldBlockCheckoutSelection,
      })
    );
  }, [isEmailValid, isPhoneValid, isEmailAvailable, isFrictionlessPhase3Eligible, dispatch]);

  const handleEmailChange = (value: string) => {
    handleChange(value, 'email');
    dispatch(authActions.setIsSignInCTADecisionPending({ isSignInCTADecisionPending: true }));
    dispatch(authActions.setShouldShowSignInCTA({ shouldShowSignInCTA: false }));
    dispatch(authActions.setFrictionlessCheckoutEmail({ email: value }));
    if (isEmailUpdateRequired) {
      // @ts-ignore
      dispatch(setContactDetailsEmail(value));
      // @ts-ignore
      dispatch(validateContactDetailsEmail());
    }
  };

  const handleNameChange = (value: string) => {
    // @ts-ignore
    dispatch(setContactDetailsName(value));
    // @ts-ignore
    dispatch(validateContactDetailsName());
  };

  const handePhoneChange = (value: string) => {
    const phoneWithCountryCode = `${frictionlessCheckoutCountry?.dial_code || '+1'}${value}`;
    handleChange(phoneWithCountryCode, 'phone');
  };

  const handleSignInLinkClicked = () => {
    SegmentIO.frictionlessLoginPhase3SignInCTALinkClicked();
    dispatch(
      authActions.setShouldShowIdentitySignInWidget({ shouldShowIdentitySignInWidget: true })
    );
    dispatch(authActions.setShouldShowSignInCTA({ shouldShowSignInCTA: false }));
    frictionlessLogger.trackSignInCTAClicked();
  };

  return (
    <React.Fragment>
      <div className="container">
        <div className="header">
          <span className="title">
            {isFrictionlessPhase3Enabled ? (
              <FormattedMessage id="FRICTIONLESS_INFO" defaultMessage="Your info" />
            ) : (
              <FormattedMessage id="FRICTIONLESS_DETAILS" defaultMessage="Your details" />
            )}
          </span>
          <div className="details-tooltip">
            <Tooltip
              title={
                <FormattedMessage
                  id="FRICTIONLESS_DETAILS_TOOLTIP"
                  defaultMessage="Your email and phone is used to verify if you have an Intuit account to offer you a faster checkout"
                />
              }
              tooltipId="details-tooltip"
            >
              <InfoCircle color={colors.gray02} width={14} height={14} />
            </Tooltip>
          </div>
        </div>
        {shouldShowContactName && (
          <div>
            <LabeledInputWithErrors
              label={<FormattedMessage id="FRICTIONLESS_FULL_NAME" defaultMessage="Full name" />}
              type="name"
              name="name"
              error={nameError}
              onChange={(e) => handleNameChange(e.target.value)}
              onBlur={() => handleBlur('name')}
              onFocus={() => handleFocus('name')}
              placeholder="Enter your full name"
            />
          </div>
        )}
        <div>
          <LabeledInputWithErrors
            label={<FormattedMessage id="FRICTIONLESS_EMAIL" defaultMessage="Email" />}
            type="email"
            name="email"
            value={email}
            error={emailError}
            onChange={(e) => handleEmailChange(e.target.value)}
            onBlur={() => handleBlur('email')}
            onFocus={() => handleFocus('email')}
            placeholder="Enter your email address"
            isLoading={shouldShowLoadingSpinner}
            disabled={isSubscriptionFLPhase2 && isGenerateFactorLoading}
          />
        </div>
        {shouldShowSignInCTA && (
          <FrictionlessSignInPhase3CTA
            email={email}
            onSignInLinkClicked={handleSignInLinkClicked}
          />
        )}
        <div>
          <LabeledInputWithErrors
            label={<FormattedMessage id="FRICTIONLESS_PHONE" defaultMessage="Phone number" />}
            type="phone"
            name="phone"
            value={phone}
            error={phoneError}
            onChange={(e) => handePhoneChange(e.target.value)}
            onBlur={() => handleBlur('phone')}
            onFocus={() => handleFocus('phone')}
            placeholder="(555) 555-5555"
            cdn={cdn}
            countryField={countryField}
            updateCountry={updateFrictionlessLoginCountry}
          />
          {shouldShowBanner && <IntuitAccountBanner />}
        </div>
        <div>
          {showPaymentMethodsHeader && (
            <span className="title change-payment-method">
              <FormattedMessage
                id="FRICTIONLESS_CHANGE_PAYMENT_METHOD"
                defaultMessage="CHANGE PAYMENT METHOD"
              />
            </span>
          )}
        </div>
      </div>
      {/*language=SCSS*/}
      <style jsx>{`
        .container {
          display: grid;
          grid-template-rows: 20px auto auto;
          grid-row-gap: 20px;

          .header {
            display: flex;
            align-items: center;
          }

          .title {
            font-family: AvenirNextforINTUIT-Demi;
            text-transform: uppercase;
            color: ${colors.gray02};
            font-size: ${fontSize.xxs};

            &.change-payment-method {
              @media (max-width: ${breakpoints.md}) {
                display: none;
              }
            }
          }

          .details-tooltip {
            margin: 2px 6px 0;
          }
        }
      `}</style>
    </React.Fragment>
  );
};

export { FrictionlessDetails };
